import cx from "classnames";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Transition } from "react-transition-group";
import DeviceManager, { DeviceManagerContext } from "@trainline-shareable/device-manager";
import { isMobileDevice, useClickOutside, usePreventScroll } from "@trainline-shareable/utils";
import { useDetectIframe, useResizeWindow } from "./hooks";
import * as styles from "./Overlay.module.css";
import OverlayBackdrop from "./overlay-backdrop/OverlayBackdrop";
import OverlayFooter from "./overlay-footer/OverlayFooter";
import OverlayHeader from "./overlay-header/OverlayHeader";
import calculatePosition from "./utils/calculatePosition";
const getOverlayContentStyles = (type) => {
  switch (type) {
    default:
    case "bubble": {
      return styles.overlayContentBubble;
    }
    case "modal": {
      return styles.overlayContentModal;
    }
    case "popover": {
      return styles.overlayContentPopover;
    }
  }
};
const getOverlayContentAnimationStyles = (type, animationState) => {
  switch (animationState) {
    case "entered": {
      if (type === "bubble") {
        return styles.overlayContentBubbleEntered;
      }
      if (type === "modal") {
        return styles.overlayContentModalEntered;
      }
      return styles.overlayContentPopoverEntered;
    }
    case "entering": {
      if (type === "bubble") {
        return styles.overlayContentBubbleEntering;
      }
      if (type === "modal") {
        return styles.overlayContentModalEntering;
      }
      return styles.overlayContentPopoverEntering;
    }
    case "exiting": {
      if (type === "bubble") {
        return styles.overlayContentBubbleExiting;
      }
      if (type === "modal") {
        return styles.overlayContentModalExiting;
      }
      return styles.overlayContentPopoverExiting;
    }
    default:
    case "exited": {
      if (type === "bubble") {
        return styles.overlayContentBubbleExited;
      }
      if (type === "modal") {
        return styles.overlayContentModalExited;
      }
      return styles.overlayContentPopoverExited;
    }
  }
};
const ClickOutsideListener = ({
  overlayContainerRef,
  onClose,
  isChild
}) => {
  useClickOutside(overlayContainerRef, onClose, isChild);
  return null;
};
const PreventScrollListener = ({
  shouldPreventScroll,
  shouldRestoreScrollPosition
}) => {
  usePreventScroll({
    isDisabled: !shouldPreventScroll,
    restoreScrollPosition: shouldRestoreScrollPosition
  });
  return null;
};
const DetectIframesListener = ({ onClose }) => {
  useDetectIframe(onClose);
  return null;
};
const Overlay = ({
  id = "modal",
  testId = "overlay",
  size,
  type = "bubble",
  hideHeader = false,
  showNubbin = false,
  position,
  headerContent,
  headerActionButtons,
  footerContent,
  footerActionButtons,
  children,
  className,
  onClose,
  animationState,
  anchorRef,
  isOpen,
  shouldMountAndUnmount,
  isChild = false
}) => {
  const [userScrolled, setOnUserScroll] = useState(false);
  const [showShadowOnTop, setShowShadowOnTop] = useState(false);
  const [showShadowOnBottom, setShowShadowOnBottom] = useState(false);
  const overlayContainerRef = useRef(null);
  const overlayBodyRef = useRef(null);
  const overlayBodyScrollableRef = useRef(null);
  const deviceManager = useContext(DeviceManagerContext);
  const isMobile = isMobileDevice(deviceManager == null ? void 0 : deviceManager.viewPort);
  const isBubbleType = type === "bubble";
  const isPopoverType = type === "popover";
  const isModalType = type === "modal";
  const shouldHideHeader = !isModalType && hideHeader;
  const shouldHideFooter = isBubbleType || !(footerContent || footerActionButtons);
  const shouldShowNubbin = !isModalType && showNubbin;
  const shouldPreventScroll = isOpen && (isModalType || isMobile);
  useEffect(() => {
    var _a, _b;
    const { height: overlayBodyScrollableHeight = 0 } = ((_a = overlayBodyScrollableRef == null ? void 0 : overlayBodyScrollableRef.current) == null ? void 0 : _a.getBoundingClientRect()) || {};
    const { height: overlayBodyHeight = 0 } = ((_b = overlayBodyRef == null ? void 0 : overlayBodyRef.current) == null ? void 0 : _b.getBoundingClientRect()) || {};
    const shouldShowBottomShadow = !shouldHideFooter && overlayBodyScrollableHeight > overlayBodyHeight;
    if (shouldShowBottomShadow) {
      setShowShadowOnBottom(true);
    }
  }, [shouldHideFooter]);
  useEffect(() => {
    const onEscKeypressHandler = (event) => {
      if (event.key === "Escape") {
        return onClose();
      }
      return false;
    };
    window.addEventListener("keydown", onEscKeypressHandler, false);
    return () => window.removeEventListener("keydown", onEscKeypressHandler, false);
  }, [onClose]);
  useEffect(() => {
    var _a;
    const shouldResetScrollForModalMobile = isMobile && isModalType && userScrolled && !shouldMountAndUnmount && !isOpen;
    if (shouldResetScrollForModalMobile) {
      if (animationState === "exiting") {
        (_a = overlayBodyRef.current) == null ? void 0 : _a.scrollTo(0, 0);
      }
      if (animationState === "exited") {
        setOnUserScroll(false);
      }
    }
  }, [isMobile, isModalType, userScrolled, animationState, isOpen, shouldMountAndUnmount]);
  const onScrollAnimationHandler = (target, scrollable) => {
    if (isModalType && !userScrolled) {
      setOnUserScroll(true);
    }
    const { bottom: targetBottom, top: targetTop } = target;
    const { bottom: scrollableBottom, top: scrollableTop } = scrollable;
    if (targetBottom > scrollableBottom && showShadowOnBottom) {
      setShowShadowOnBottom(false);
    } else if (!showShadowOnBottom && targetBottom <= scrollableBottom) {
      setShowShadowOnBottom(true);
    }
    if (targetTop < scrollableTop && showShadowOnTop) {
      setShowShadowOnTop(false);
    } else if (!showShadowOnTop && targetTop >= scrollableTop) {
      setShowShadowOnTop(true);
    }
  };
  const onScrollHandler = (e) => {
    e.persist();
    window.requestAnimationFrame(() => {
      var _a;
      if (overlayBodyScrollableRef.current) {
        onScrollAnimationHandler(e.target.getBoundingClientRect(), (_a = overlayBodyScrollableRef.current) == null ? void 0 : _a.getBoundingClientRect());
      }
    });
  };
  const calculatePositionForFloatingOverlay = useCallback(() => !isModalType && position && (anchorRef == null ? void 0 : anchorRef.current) ? calculatePosition({
    target: anchorRef.current.getBoundingClientRect(),
    position,
    type,
    withNubbin: showNubbin
  }) : void 0, [anchorRef, isModalType, position, showNubbin, type]);
  const debouncedPositionAfterResize = useResizeWindow(calculatePositionForFloatingOverlay);
  return /* @__PURE__ */ React.createElement("div", {
    "data-testid": `${testId}-container`,
    "data-overlay-is-child": isChild ? "" : void 0,
    className: cx(styles.overlayContainer, {
      [styles.overlayContainerBubble]: isBubbleType,
      [styles.overlayContainerModal]: isModalType,
      [styles.overlayContainerPopover]: isPopoverType,
      [styles.overlayContainerExited]: animationState === "exited"
    })
  }, isOpen && animationState === "entered" && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(PreventScrollListener, {
    shouldPreventScroll,
    shouldRestoreScrollPosition: isMobile || isModalType
  }), /* @__PURE__ */ React.createElement(DetectIframesListener, {
    onClose
  }), !isModalType && /* @__PURE__ */ React.createElement(ClickOutsideListener, {
    overlayContainerRef,
    onClose,
    isChild
  })), /* @__PURE__ */ React.createElement(OverlayBackdrop, {
    onClose,
    type,
    animationState
  }), /* @__PURE__ */ React.createElement("div", {
    id,
    role: "dialog",
    "aria-modal": isModalType,
    style: {
      ...debouncedPositionAfterResize || calculatePositionForFloatingOverlay(),
      ...!isModalType && size ? { minWidth: `${size}px`, width: "100%", maxWidth: `${size}px` } : {}
    },
    className: cx(styles.overlayContent, getOverlayContentStyles(type), getOverlayContentAnimationStyles(type, animationState), {
      [styles.overlayContentFullHeight]: isPopoverType || isModalType && userScrolled,
      [styles.nubbin]: shouldShowNubbin,
      [styles.nubbinPositionBottomLeft]: shouldShowNubbin && position === "bottom_left",
      [styles.nubbinPositionBottomCenter]: shouldShowNubbin && position === "bottom_center",
      [styles.nubbinPositionBottomRight]: shouldShowNubbin && position === "bottom_right",
      [styles.nubbinPositionLeft]: shouldShowNubbin && position === "left",
      [styles.nubbinPositionRight]: shouldShowNubbin && position === "right"
    }, className),
    ref: overlayContainerRef,
    "data-testid": testId
  }, !shouldHideHeader && /* @__PURE__ */ React.createElement(OverlayHeader, {
    type,
    content: headerContent,
    actionButtons: headerActionButtons,
    onClose,
    showShadow: showShadowOnTop
  }), children && /* @__PURE__ */ React.createElement("div", {
    className: cx(styles.overlayBody, {
      [styles.overlayBodyBubble]: isBubbleType,
      [styles.overlayBodyNoHeader]: shouldHideHeader,
      [styles.overlayBodyNoFooter]: shouldHideFooter
    }),
    "data-testid": `${testId}-body`,
    ref: overlayBodyRef,
    ...!isBubbleType ? { onScroll: onScrollHandler } : {}
  }, /* @__PURE__ */ React.createElement("div", {
    className: styles.overlayBodyScrollable,
    "data-testid": `${testId}-body-scrollable`,
    ref: overlayBodyScrollableRef
  }, children)), !shouldHideFooter && /* @__PURE__ */ React.createElement(OverlayFooter, {
    content: footerContent,
    actionButtons: footerActionButtons,
    showShadow: showShadowOnBottom
  })));
};
const OverlayContainer = ({
  isOpen,
  shouldMountAndUnmount = true,
  onExited,
  onExiting,
  ...props
}) => {
  const [portalElement, setPortalElement] = useState(void 0);
  useEffect(() => {
    const pivot = document.createElement("div");
    pivot.setAttribute("data-testid", "overlay-portal");
    document.body.appendChild(pivot);
    setPortalElement(pivot);
    return () => {
      document.body.removeChild(pivot);
    };
  }, []);
  const content = /* @__PURE__ */ React.createElement(DeviceManager, null, /* @__PURE__ */ React.createElement(Transition, {
    in: isOpen,
    appear: true,
    timeout: 300,
    mountOnEnter: shouldMountAndUnmount,
    unmountOnExit: shouldMountAndUnmount,
    onExited,
    onExiting
  }, (state) => /* @__PURE__ */ React.createElement(Overlay, {
    ...props,
    isOpen,
    animationState: state,
    shouldMountAndUnmount
  })));
  if (portalElement === void 0) {
    return null;
  }
  return createPortal(content, portalElement);
};
var Overlay_default = OverlayContainer;
export {
  Overlay,
  OverlayContainer,
  Overlay_default as default
};
